import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";
import { SkeletonStats, SkeletonBlock } from "../../components/skeleton";
import { ChipInput } from "../../components/chip-input";

interface VoiceProfile {
  id: string;
  name: string;
  profile_json: string;
  status: "pending" | "training" | "ready" | "failed";
  source_count: number;
  total_chars: number;
  quality_score: number;
  quality_rating: "weak" | "fair" | "good" | "strong";
  quality_hints: string[];
  created_at: number;
  updated_at: number;
}

interface SourceRow {
  id: string;
  source_title: string | null;
  preview: string;
  length: number;
  created_at: number;
}

const MIN_SOURCES = 3;

export default function VoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [profile, setProfile] = useState<VoiceProfile | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonDraft, setJsonDraft] = useState("");
  const [savingJson, setSavingJson] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const [showAddSource, setShowAddSource] = useState(false);
  const [showImportUrl, setShowImportUrl] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importMaxItems, setImportMaxItems] = useState(5);
  const [importing, setImporting] = useState(false);
  const [newSource, setNewSource] = useState({ source_title: "", content: "" });
  const [addingSource, setAddingSource] = useState(false);

  const polling = profile?.status === "training";

  useEffect(() => {
    if (!id) return;
    void load();
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [id, polling]);

  async function load() {
    if (!id) return;
    try {
      const [p, s] = await Promise.all([
        api.get<VoiceProfile>(`/api/voice-profiles/${id}`),
        api.get<{ sources: SourceRow[] }>(`/api/voice-profiles/${id}/sources`),
      ]);
      setProfile(p);
      setSources(s.sources);
    } finally {
      setLoading(false);
    }
  }

  const parsedProfile = useMemo(() => {
    if (!profile?.profile_json) return null;
    try {
      const obj = JSON.parse(profile.profile_json);
      if (obj && typeof obj === "object" && Object.keys(obj).length > 0) {
        return obj as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }, [profile?.profile_json]);

  if (loading || !profile) {
    return (
      <div>
        <SkeletonBlock lines={2} />
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="card">
            <SkeletonStats rows={4} />
          </div>
          <div className="card">
            <SkeletonStats rows={4} />
          </div>
        </div>
      </div>
    );
  }

  async function train() {
    if (!id) return;
    try {
      await api.post(`/api/voice-profiles/${id}/train`);
      toast.info("Training started — this usually takes 10–40 seconds.");
      void load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function remove() {
    if (!id) return;
    const ok = await confirm({
      title: "Delete this voice profile?",
      message:
        "Projects already using this voice keep working, but no future projects can use it. This can't be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/voice-profiles/${id}`);
      toast.success("Voice profile deleted.");
      navigate("/app/voices");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function saveJson() {
    if (!id) return;
    setJsonError(null);
    try {
      JSON.parse(jsonDraft);
    } catch {
      setJsonError("Invalid JSON.");
      return;
    }
    setSavingJson(true);
    try {
      await api.patch(`/api/voice-profiles/${id}`, { profile_json: jsonDraft });
      setShowJsonEditor(false);
      toast.success("Style profile saved.");
      void load();
    } catch (err) {
      setJsonError(apiErrorMessage(err));
    } finally {
      setSavingJson(false);
    }
  }

  async function addSource() {
    if (!id) return;
    if (newSource.content.trim().length < 50) {
      toast.error("Content must be at least 50 characters.");
      return;
    }
    setAddingSource(true);
    try {
      await api.post(`/api/voice-profiles/${id}/sources`, {
        content: newSource.content,
        source_title: newSource.source_title.trim() || undefined,
      });
      setNewSource({ source_title: "", content: "" });
      setShowAddSource(false);
      toast.success("Sample added.");
      void load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setAddingSource(false);
    }
  }

  async function importFromUrl() {
    if (!id) return;
    const u = importUrl.trim();
    if (!u) return;
    setImporting(true);
    try {
      const res = await api.post<{ inserted: number }>(
        `/api/voice-profiles/${id}/sources/import-url`,
        { url: u, max_items: importMaxItems }
      );
      toast.success(
        `Imported ${res.inserted} ${res.inserted === 1 ? "sample" : "samples"} from URL.`
      );
      setImportUrl("");
      setShowImportUrl(false);
      void load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setImporting(false);
    }
  }

  async function removeSource(sid: string) {
    if (!id) return;
    const ok = await confirm({
      title: "Remove this sample?",
      message: "This sample won't be used in future training.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/voice-profiles/${id}/sources/${sid}`);
      toast.success("Sample removed.");
      void load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <div>
      <button
        onClick={() => navigate("/app/voices")}
        className="mb-4 text-sm text-ink-500 hover:text-ink-900"
      >
        ← Voices
      </button>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="page-title">{profile.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-500">
            <span>Updated {new Date(profile.updated_at).toLocaleString()}</span>
            <span>·</span>
            <span>
              {profile.source_count}{" "}
              {profile.source_count === 1 ? "sample" : "samples"}
            </span>
            <span>·</span>
            <StatusBadge status={profile.status} />
          </div>
          <QualityCard
            score={profile.quality_score}
            rating={profile.quality_rating}
            hints={profile.quality_hints}
          />
        </div>
        <div className="flex gap-2">
          {profile.status !== "training" && (
            <button
              onClick={train}
              className="btn-secondary"
              disabled={profile.source_count < MIN_SOURCES}
              title={
                profile.source_count < MIN_SOURCES
                  ? `Need at least ${MIN_SOURCES} samples`
                  : ""
              }
            >
              {profile.status === "ready" ? "Retrain" : "Train"}
            </button>
          )}
          <button onClick={remove} className="btn-secondary text-red-600">
            Delete
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <section className="card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink-900">Style profile</h2>
            {profile.status === "ready" && (
              <button
                onClick={() => {
                  setJsonDraft(
                    JSON.stringify(JSON.parse(profile.profile_json || "{}"), null, 2)
                  );
                  setShowJsonEditor(true);
                  setJsonError(null);
                }}
                className="text-xs text-ink-500 hover:text-ink-900"
              >
                Edit JSON
              </button>
            )}
          </div>

          {profile.status === "training" && (
            <p className="text-sm text-ink-500">
              Training… this usually takes 10-40 seconds.
            </p>
          )}
          {profile.status === "pending" && (
            <p className="text-sm text-ink-500">
              Add at least {MIN_SOURCES} samples and click Train.
            </p>
          )}
          {profile.status === "failed" && (
            <p className="text-sm text-red-600">
              Training failed. Try retraining.
            </p>
          )}
          {profile.status === "ready" && parsedProfile && (
            <ProfileView profile={parsedProfile} />
          )}
        </section>

        <section className="card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-ink-900">
              Source samples ({sources.length})
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => {
                  setShowImportUrl((v) => !v);
                  if (!showImportUrl) setShowAddSource(false);
                }}
                className={`btn-secondary text-sm ${
                  showImportUrl ? "bg-ink-100" : ""
                }`}
              >
                {showImportUrl ? "Cancel" : "🔗 Import from URL"}
              </button>
              <button
                onClick={() => {
                  setShowAddSource((v) => !v);
                  if (!showAddSource) setShowImportUrl(false);
                }}
                className={`btn-secondary text-sm ${
                  showAddSource ? "bg-ink-100" : ""
                }`}
              >
                {showAddSource ? "Cancel" : "+ Add sample"}
              </button>
            </div>
          </div>

          {showImportUrl && (
            <div className="mb-4 space-y-2 rounded border border-ink-200 p-3">
              <input
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://example.com/blog/post or https://example.com/feed.xml"
                className="input"
                maxLength={1000}
                type="url"
              />
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-ink-500">
                  Max items:
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={importMaxItems}
                    onChange={(e) =>
                      setImportMaxItems(
                        Math.max(1, Math.min(10, Number(e.target.value) || 5))
                      )
                    }
                    className="ml-2 w-16 rounded border border-ink-300 px-2 py-1 text-sm"
                  />
                </label>
                <p className="flex-1 text-xs text-ink-500">
                  Paste an article URL or RSS/Atom feed. We'll extract clean text.
                </p>
                <button
                  onClick={importFromUrl}
                  className="btn-primary"
                  disabled={importing || importUrl.trim().length === 0}
                >
                  {importing ? "Importing…" : "Import"}
                </button>
              </div>
            </div>
          )}

          {showAddSource && (
            <div className="mb-4 space-y-2 rounded border border-ink-200 p-3">
              <input
                value={newSource.source_title}
                onChange={(e) =>
                  setNewSource((s) => ({ ...s, source_title: e.target.value }))
                }
                placeholder="Title (optional)"
                className="input"
                maxLength={200}
              />
              <textarea
                value={newSource.content}
                onChange={(e) =>
                  setNewSource((s) => ({ ...s, content: e.target.value }))
                }
                placeholder="Paste sample content…"
                className="input min-h-[120px] font-mono text-sm"
                maxLength={100000}
              />
              <div className="flex justify-end">
                <button
                  onClick={addSource}
                  className="btn-primary"
                  disabled={addingSource || newSource.content.trim().length < 50}
                >
                  {addingSource ? "Adding…" : "Add"}
                </button>
              </div>
            </div>
          )}

          {sources.length === 0 ? (
            <p className="text-sm text-ink-500">No samples yet.</p>
          ) : (
            <ul className="space-y-2">
              {sources.map((s) => (
                <li
                  key={s.id}
                  className="rounded border border-ink-200 bg-white p-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink-900">
                        {s.source_title || "Untitled sample"}
                      </div>
                      <div className="text-xs text-ink-500">
                        {s.length.toLocaleString()} chars ·{" "}
                        {new Date(s.created_at).toLocaleDateString()}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-ink-700">
                        {s.preview}
                        {s.length > s.preview.length && "…"}
                      </p>
                    </div>
                    <button
                      onClick={() => removeSource(s.id)}
                      className="ml-3 text-xs text-ink-500 hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {profile.status === "ready" && parsedProfile ? (
        <CustomizePhrases
          profileId={profile.id}
          profile={parsedProfile}
          onSaved={() => void load()}
        />
      ) : null}

      {showJsonEditor && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-6 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-3xl rounded-2xl border border-ink-200/60 bg-white p-6 shadow-xl animate-scale-in">
            <h3 className="mb-3 text-lg font-semibold text-ink-900">
              Edit style profile
            </h3>
            <p className="mb-3 text-xs text-ink-500">
              Manual edits are kept until the next retraining.
            </p>
            <textarea
              value={jsonDraft}
              onChange={(e) => setJsonDraft(e.target.value)}
              className="input min-h-[400px] font-mono text-xs"
              spellCheck={false}
            />
            {jsonError && (
              <p className="mt-2 text-xs text-red-600">{jsonError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowJsonEditor(false)}
                className="btn-secondary"
                disabled={savingJson}
              >
                Cancel
              </button>
              <button
                onClick={saveJson}
                className="btn-primary"
                disabled={savingJson}
              >
                {savingJson ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomizePhrases({
  profileId,
  profile,
  onSaved,
}: {
  profileId: string;
  profile: Record<string, unknown>;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [signature, setSignature] = useState<string[]>(arrFmt(profile.signature_phrases));
  const [openers, setOpeners] = useState<string[]>(arrFmt(profile.favorite_openers));
  const [closers, setClosers] = useState<string[]>(arrFmt(profile.favorite_closers));
  const [banned, setBanned] = useState<string[]>(arrFmt(profile.banned_words));
  const [saving, setSaving] = useState(false);

  const initial = useMemo(
    () => ({
      signature: arrFmt(profile.signature_phrases),
      openers: arrFmt(profile.favorite_openers),
      closers: arrFmt(profile.favorite_closers),
      banned: arrFmt(profile.banned_words),
    }),
    [profile]
  );

  const dirty =
    !sameArr(signature, initial.signature) ||
    !sameArr(openers, initial.openers) ||
    !sameArr(closers, initial.closers) ||
    !sameArr(banned, initial.banned);

  function handleReset() {
    setSignature(initial.signature);
    setOpeners(initial.openers);
    setClosers(initial.closers);
    setBanned(initial.banned);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const next = {
        ...profile,
        signature_phrases: signature,
        favorite_openers: openers,
        favorite_closers: closers,
        banned_words: banned,
      };
      await api.patch(`/api/voice-profiles/${profileId}`, {
        profile_json: JSON.stringify(next),
      });
      toast.success("Phrases updated.");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card mt-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">Customize phrases</h2>
          <p className="mt-1 text-xs text-ink-500">
            Manual edits override the trained values until next retraining.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={!dirty || saving}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : "Save phrases"}
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <PhraseField
          label="Signature phrases"
          hint="Sayings that feel uniquely yours. The model will treat these as banned-verbatim to keep them sounding natural rather than copied."
          value={signature}
          onChange={setSignature}
          placeholder="e.g. plan accordingly: lightly"
        />
        <PhraseField
          label="Favorite openers"
          hint="Lines you tend to start posts with."
          value={openers}
          onChange={setOpeners}
          placeholder="e.g. Most teams measure the wrong thing."
        />
        <PhraseField
          label="Favorite closers"
          hint="Lines you tend to end on."
          value={closers}
          onChange={setClosers}
          placeholder="e.g. The rest is detail."
        />
        <PhraseField
          label="Banned words"
          hint="Words you never use. Generation will avoid these and their close synonyms."
          value={banned}
          onChange={setBanned}
          placeholder="e.g. synergy"
          variant="warn"
        />
      </div>
    </section>
  );
}

function PhraseField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  variant,
}: {
  label: string;
  hint: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  variant?: "default" | "warn";
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-ink-900">{label}</label>
      <ChipInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        variant={variant}
      />
      <p className="mt-1 text-xs text-ink-500">{hint}</p>
    </div>
  );
}

function sameArr(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function ProfileView({ profile }: { profile: Record<string, unknown> }) {
  const tone = String(profile.tone ?? "—");
  const formality = numFmt(profile.formality);
  const senAvg = numFmt(profile.sentence_length_avg);
  const senP90 = numFmt(profile.sentence_length_p90);
  const emoji = String(profile.emoji_usage ?? "—");
  const firstPerson = numFmt(profile.first_person_ratio);
  const punchline = String(profile.punchline_pattern ?? "—");
  const openers = arrFmt(profile.favorite_openers);
  const closers = arrFmt(profile.favorite_closers);
  const banned = arrFmt(profile.banned_words);
  const devices = arrFmt(profile.rhetorical_devices);

  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Tone" value={tone} />
        <Field label="Formality" value={formality} />
        <Field label="Avg sentence" value={`${senAvg} words`} />
        <Field label="P90 sentence" value={`${senP90} words`} />
        <Field label="Emoji" value={emoji} />
        <Field label="First-person" value={firstPerson} />
      </div>
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-500">
          Punchline pattern
        </div>
        <div className="text-ink-900">{punchline}</div>
      </div>
      {openers.length > 0 && <ChipList label="Openers" items={openers} />}
      {closers.length > 0 && <ChipList label="Closers" items={closers} />}
      {devices.length > 0 && <ChipList label="Devices" items={devices} />}
      {banned.length > 0 && (
        <ChipList label="Avoid" items={banned} variant="warn" />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="mb-0.5 text-xs font-medium uppercase tracking-wide text-ink-500">
        {label}
      </div>
      <div className="text-ink-900">{String(value)}</div>
    </div>
  );
}

function ChipList({
  label,
  items,
  variant,
}: {
  label: string;
  items: string[];
  variant?: "warn";
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-500">
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((it) => (
          <span
            key={it}
            className={`rounded-full px-2 py-0.5 text-xs ${
              variant === "warn"
                ? "bg-red-50 text-red-700"
                : "bg-ink-100 text-ink-700"
            }`}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: VoiceProfile["status"] }) {
  const map: Record<VoiceProfile["status"], string> = {
    pending: "bg-ink-100 text-ink-700",
    training: "bg-amber-100 text-amber-700",
    ready: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700",
  };
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${map[status]}`}>{status}</span>
  );
}

function QualityCard({
  score,
  rating,
  hints,
}: {
  score: number;
  rating: VoiceProfile["quality_rating"];
  hints: string[];
}) {
  const colorMap: Record<VoiceProfile["quality_rating"], string> = {
    weak: "bg-red-500",
    fair: "bg-yellow-500",
    good: "bg-blue-500",
    strong: "bg-emerald-500",
  };
  const labelMap: Record<VoiceProfile["quality_rating"], string> = {
    weak: "Weak",
    fair: "Fair",
    good: "Good",
    strong: "Strong",
  };
  return (
    <div className="mt-4 max-w-md rounded-lg border border-ink-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
          Profile quality
        </span>
        <span className="text-xs font-semibold text-ink-900">
          {score}/100 · {labelMap[rating]}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-200">
        <div
          className={`h-1.5 transition-all ${colorMap[rating]}`}
          style={{ width: `${Math.max(2, score)}%` }}
        />
      </div>
      {hints.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs text-ink-500">
          {hints.map((h, i) => (
            <li key={i} className="flex gap-1">
              <span className="text-ink-400">•</span>
              <span>{h}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function numFmt(v: unknown): string {
  if (typeof v === "number") return v.toFixed(v < 1 ? 2 : 0);
  return String(v ?? "—");
}

function arrFmt(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}
